import React, { useEffect, useRef, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Box, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import CriticalNotifications from "./CriticalNotifications";
import WarningNotifications from "./WarningNotifications";
import { EventsIndicator } from "./EventsIndicator";
import Divider from "@material-ui/core/Divider/Divider";
import {
  failedFirebaseDashboard,
  setCommunityEvents,
  setRFIDTags,
  startLoadingDashboard,
  startMonitoringNewEvents,
} from "../dashboardSlice";
import { useDispatch } from "react-redux";

import { useAppSelector } from "../../app/appHooks";
import { IAppState } from "../../../helpers/store";
import {
  dataPaths,
  listenToList,
  listenToRFIDTags,
} from "../../../helpers/firebase";
import {
  getEventsToUpdateFirebase,
  getUserUnitAccess,
} from "../../../helpers/utils";
import useEventTypesCount from "../hooks/useEventTypes";
import useChangeFeed from "../hooks/useChangeFeed";
import {
  ACTIVE_PANEL_WIDTH,
  SOUND_WINDOW_MS,
  HEARTBEAT_MS,
  TabTypes,
  STALL_THRESHOLD_MS,
  SUPPRESS_AFTER_RESUME_MS,
} from "../../../helpers/constants";
import useOrganization from "./hooks/useOrganization";
import usePanelsHeight from "./hooks/usePanelsHeight";
import { ORG_NOTIFICATIONS_PER_COMMUNITY } from "../../../services/constants";
import { minToolbarHeight } from "../../app/App";
import { NotificationSounds } from "../../../helpers/soundImports";
import { EpochFromISOStringUTC } from "../../../helpers/datetime";

const useStyles = makeStyles<Theme, Boolean>((theme: Theme) =>
  createStyles({
    notificationsContainer: {
      display: "flex",
      width: "100%",
      borderLeft: `1px solid ${theme.palette.divider}`,
      height: (showBanner) =>
        `calc(100vh - ${minToolbarHeight(showBanner, theme)})`,
      padding: theme.spacing(2.8, 1),
      flexDirection: "column",
      backgroundColor: theme.palette.light.light,
    },
    titleContainer: {
      display: "flex",
      marginBottom: theme.spacing(1),
      alignItems: "center",
    },
    indicatorsContainer: {
      display: "flex",
      "& > *": {
        margin: theme.spacing(1),
      },
    },
    colorSecondary: {
      backgroundColor: theme.palette.dark.main,
    },
    notificationEventContainer: {
      display: "flex",
      width: "100%",
      flexDirection: "column",
      overflowY: "auto",
      height: "100%",
    },
  })
);

export default function Notifications(props: IProps) {
  const { addTab } = props;
  const showBanner = useAppSelector((state) => state.headerState.showBanner);

  /* ---- State ---- */
  const [initHardEvents, setInitHardEvents] = useState(false);
  const [initSoftEvents, setInitSoftEvents] = useState(false);

  /* ---- Hooks ---- */
  const classes = useStyles(showBanner);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedOrganizationID, orgsCommunities } = useOrganization();

  /* ---- Selectors ---- */
  const firebaseAuth = useAppSelector((state) => state.loginState.firebaseAuth);

  const communityEvents = useAppSelector(
    (state) => state.dashboardState.communityEvents || []
  );

  const selectedCommunityID = useAppSelector(
    (state) => state.headerState.selectedCommunity?.id
  );

  const installerRole = useAppSelector(
    (state) => state.headerState.installerRole
  );

  const soundEnabled = useAppSelector(
    (state) => state.headerState.selectedCommunity?.notification_sound_enabled
  );

  const currentUser = useAppSelector(
    (state: IAppState) => state.headerState.user
  );

  const ref = useRef(null);

  const { criticalEventsCount, warningEventsCount } = useEventTypesCount(
    communityEvents
  );

  const { criticalPanelHeight, warningPanelHeight } = usePanelsHeight(
    warningEventsCount,
    criticalEventsCount,
    ref.current
  );

  const initialized = useRef(false);
  const lastSeenCreatedAtRef = useRef<number | null>(null);
  const lastSeenIdRef = useRef<string | null>(null);
  const lastBeatRef = useRef<number>(Date.now());
  const suppressUntilRef = useRef<number>(0);

  const changeFeedMode = useChangeFeed((event) => {
    const timeCreatedEpoch = EpochFromISOStringUTC(event.time_created);
    if (
      Number.isFinite(timeCreatedEpoch) &&
      (lastSeenCreatedAtRef.current === null ||
        timeCreatedEpoch > lastSeenCreatedAtRef.current)
    ) {
      lastSeenCreatedAtRef.current = timeCreatedEpoch;
      lastSeenIdRef.current = event.id;
    }
  });
  // The Firebase listeners only run when the change feed is off
  const listenersOn = changeFeedMode === "off";

  /* ---- Effects ---- */
  useEffect(() => {
    initialized.current = false;
  }, [selectedCommunityID]);

  useEffect(() => {
    if (
      selectedCommunityID !== undefined &&
      firebaseAuth &&
      listenersOn &&
      !installerRole &&
      currentUser &&
      currentUser.roles
    ) {
      const communityIds = new Set();
      const unitIds = new Set();
      currentUser.roles.forEach((role) => {
        if (role.role.all_units) {
          communityIds.add(role.role.community_id);
        } else {
          if (role.units) {
            role.units.forEach((unit) => {
              unitIds.add(unit.id);
            });
          }
        }
      });
      dispatch(startLoadingDashboard());
      return listenToList(
        dataPaths.unresolvedEvents(selectedCommunityID),
        (vals) => {
          let _records = getEventsToUpdateFirebase(vals);
          _records = _records.filter((record) => {
            const eventUnitID = record.unit_id;
            const eventCommunityID = record.community_id;
            return (
              communityIds.has(eventCommunityID) || unitIds.has(eventUnitID)
            );
          });

          const recordsWithMs = _records
            .map((r) => ({
              r,
              timeCreatedEpoch: EpochFromISOStringUTC(r.time_created),
            }))
            .filter((x) => Number.isFinite(x.timeCreatedEpoch));

          const latest = recordsWithMs.length
            ? recordsWithMs.reduce((a, b) =>
                b.timeCreatedEpoch > a.timeCreatedEpoch ? b : a
              )
            : null;

          if (!initialized.current) {
            initialized.current = true;
            lastSeenCreatedAtRef.current = null;
            lastSeenIdRef.current = null;
          } else {
            if (latest) {
              if (
                lastSeenCreatedAtRef.current === null ||
                latest.timeCreatedEpoch > lastSeenCreatedAtRef.current
              ) {
                lastSeenCreatedAtRef.current = latest.timeCreatedEpoch;
                lastSeenIdRef.current = latest.r.id;
              }
            }
          }

          dispatch(setCommunityEvents(_records));
          if (!initHardEvents) setInitHardEvents(true);
        },
        { onError: () => dispatch(failedFirebaseDashboard()) }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommunityID, firebaseAuth, listenersOn]);

  useEffect(() => {
    const clearSeen = () => {
      lastSeenCreatedAtRef.current = null;
      lastSeenIdRef.current = null;
    };
    const interval = window.setInterval(() => {
      const now = Date.now();
      const gap = now - lastBeatRef.current;
      lastBeatRef.current = now;

      const stalled = gap > STALL_THRESHOLD_MS;
      if (stalled || now < suppressUntilRef.current) {
        if (stalled) {
          suppressUntilRef.current = now + SUPPRESS_AFTER_RESUME_MS;
        }
        clearSeen();
        return;
      }

      const createdAt = lastSeenCreatedAtRef.current;
      if (!soundEnabled || createdAt == null) {
        clearSeen();
        return;
      }

      if (now - createdAt <= SOUND_WINDOW_MS) {
        const sound = new Audio(NotificationSounds.PagerShort);
        sound.play().catch((error) => {
          console.error("Error playing sound:", error);
        });
      }

      clearSeen();
    }, HEARTBEAT_MS);

    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      selectedCommunityID !== undefined &&
      currentUser !== undefined &&
      firebaseAuth &&
      listenersOn &&
      !installerRole &&
      currentUser &&
      currentUser.roles
    ) {
      const communityIds = new Set();
      const unitIds = new Set();
      currentUser.roles.forEach((role) => {
        if (role.role.all_units) {
          communityIds.add(role.role.community_id);
        } else {
          if (role.units) {
            role.units.forEach((unit) => {
              unitIds.add(unit.id);
            });
          }
        }
      });
      dispatch(startLoadingDashboard());
      return listenToList(
        dataPaths.unreadSoftEvents(selectedCommunityID, currentUser.id),
        (vals) => {
          let _records = getEventsToUpdateFirebase(vals, true);
          dispatch(setCommunityEvents(_records));
          if (!initSoftEvents) setInitSoftEvents(true);
        },
        { onError: () => dispatch(failedFirebaseDashboard()) }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommunityID, firebaseAuth, currentUser, listenersOn]);

  // Signal the initialization is done and start monitoring events
  useEffect(() => {
    if (initHardEvents && initSoftEvents) {
      dispatch(startMonitoringNewEvents());
    }
  }, [dispatch, initHardEvents, initSoftEvents]);

  // Organization effects
  useEffect(() => {
    if (
      selectedOrganizationID !== undefined &&
      orgsCommunities !== undefined &&
      firebaseAuth &&
      listenersOn &&
      !installerRole
    ) {
      dispatch(startLoadingDashboard());
      const unsubscribes = orgsCommunities.map((community) =>
        listenToList(
          dataPaths.unresolvedEvents(community.id),
          (vals) => {
            let _records = getEventsToUpdateFirebase(vals);
            dispatch(setCommunityEvents(_records));
          },
          {
            query: {
              orderByKey: true,
              limitToFirst: ORG_NOTIFICATIONS_PER_COMMUNITY,
            },
            onError: () => dispatch(failedFirebaseDashboard()),
          }
        )
      );
      return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizationID, firebaseAuth, listenersOn]);

  useEffect(() => {
    if (
      selectedOrganizationID !== undefined &&
      orgsCommunities !== undefined &&
      currentUser !== undefined &&
      firebaseAuth &&
      listenersOn &&
      !installerRole
    ) {
      dispatch(startLoadingDashboard());
      const unsubscribes = orgsCommunities.map((community) =>
        listenToList(
          dataPaths.unreadSoftEvents(community.id, currentUser.id),
          (vals) => {
            let _records = getEventsToUpdateFirebase(vals, true);
            dispatch(setCommunityEvents(_records));
          },
          {
            query: {
              orderByKey: true,
              limitToFirst: ORG_NOTIFICATIONS_PER_COMMUNITY,
            },
            onError: () => dispatch(failedFirebaseDashboard()),
          }
        )
      );
      return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizationID, firebaseAuth, currentUser, listenersOn]);

  // Users limited to some units only read their own units tags, organization
  // users have no unit list but can read every tag
  const rfidAccess = getUserUnitAccess(
    currentUser?.roles ?? [],
    selectedCommunityID
  );
  const rfidUnitIDs =
    !rfidAccess.hasAllUnits && rfidAccess.unitIds.size > 0
      ? Array.from(rfidAccess.unitIds).sort().join(",")
      : "";

  useEffect(() => {
    if (selectedCommunityID && firebaseAuth) {
      // Tags are secondary, so a failed read is logged without failing the dashboard
      return listenToRFIDTags(
        selectedCommunityID,
        (data) => {
          if (!data) return;
          dispatch(setRFIDTags(data));
        },
        (error) => console.error(error),
        rfidUnitIDs ? rfidUnitIDs.split(",") : undefined
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommunityID, firebaseAuth, rfidUnitIDs]);

  const innerListWidth = ACTIVE_PANEL_WIDTH - 18;
  return (
    <div
      className={classes.notificationsContainer}
      id="notifications-container"
    >
      <Typography
        id="notification-title"
        component="div"
        className={classes.titleContainer}
      >
        <Box fontWeight="fontWeightBold" fontSize="h6.fontSize">
          {t("active_notifications")}
        </Box>
        <EventsIndicator
          amountCriticalEvents={criticalEventsCount}
          amountWarningEvents={warningEventsCount}
        />
      </Typography>
      <div
        id="inner-notifications-container"
        className={classes.notificationEventContainer}
        ref={ref}
      >
        {warningEventsCount !== 0 || criticalEventsCount !== 0 ? (
          <React.Fragment>
            <CriticalNotifications
              addTab={addTab}
              unitId={null}
              height={criticalPanelHeight}
              // Width of the active panel minus the padding
              width={innerListWidth}
            />
            {warningEventsCount > 0 && criticalEventsCount > 0 && <Divider />}

            <WarningNotifications
              addTab={addTab}
              unitId={null}
              height={warningPanelHeight}
              width={innerListWidth}
            />
          </React.Fragment>
        ) : (
          <Box fontWeight="fontWeightBold" textAlign="center" marginTop={4}>
            {t("no_active_notifications")}
          </Box>
        )}
      </div>
    </div>
  );
}

interface IProps {
  addTab: (id: string, type: TabTypes) => void;
}
